// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AccountingCostFormValues } from '@/accounting/utilities/cost-breakdown-form';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { COST_BREAKDOWN_FIELDS } from '@/constants/accounting-form-fields';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';

/** Drops the trailing "($)" marker; the field shows the accounting currency itself. */
function moneyLabel(label: string): string {
  return label.replace(' ($)', '');
}

interface Props {
  /** Accounting currency locked onto every breakdown field. */
  costCurrencyCode: IsoCurrencyCode;
  /** Bumped on mode toggle/reset to force the inputs to remount. */
  toggleCount: number;
}

/**
 * Advanced cost-breakdown inputs (acquisition and deployment costs) shared by
 * the item and asset accounting forms. Callers own the surrounding Collapse and
 * the accounting-currency selector; every field is denominated in costCurrencyCode.
 *
 * Must be rendered inside a FormProvider whose form values include the
 * accounting cost fields (AccountingCostFormValues).
 */
export function CostBreakdownFields(props: Props) {
  const { costCurrencyCode, toggleCount } = props;
  const { control } = useFormContext<AccountingCostFormValues>();

  return (
    <Box paddingTop={4}>
      <Box
        display="grid"
        gridTemplateColumns="1fr"
        paddingX={{ xs: 0, md: '10%', lg: '20%' }}
      >
        <Typography variant="subtitle1" paddingBottom={2}>
          Acquisition costs
        </Typography>
        <Box display="grid" gridTemplateColumns="1fr" rowGap={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormCurrencyTextField
              key={`billOfMaterials-${toggleCount}`}
              fullWidth
              label={moneyLabel(COST_BREAKDOWN_FIELDS.billOfMaterials.label)}
              name="billOfMaterials"
              control={control}
              lockTo={costCurrencyCode}
              compact
            />
            <Tooltip
              arrow
              placement="top"
              title={COST_BREAKDOWN_FIELDS.billOfMaterials.tooltip}
            >
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormCurrencyTextField
              key={`directLabor-${toggleCount}`}
              fullWidth
              label={moneyLabel(COST_BREAKDOWN_FIELDS.directLabor.label)}
              name="directLabor"
              control={control}
              lockTo={costCurrencyCode}
              compact
            />
            <Tooltip
              arrow
              placement="top"
              title={COST_BREAKDOWN_FIELDS.directLabor.tooltip}
            >
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormCurrencyTextField
              key={`allocatedIndirect-${toggleCount}`}
              fullWidth
              label={moneyLabel(COST_BREAKDOWN_FIELDS.allocatedIndirect.label)}
              name="allocatedIndirect"
              control={control}
              lockTo={costCurrencyCode}
              compact
            />
            <Tooltip
              arrow
              placement="top"
              title={COST_BREAKDOWN_FIELDS.allocatedIndirect.tooltip}
            >
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormCurrencyTextField
              key={`freightInbound-${toggleCount}`}
              fullWidth
              label={moneyLabel(COST_BREAKDOWN_FIELDS.freightInbound.label)}
              name="freightInbound"
              control={control}
              lockTo={costCurrencyCode}
              compact
            />
            <Tooltip
              arrow
              placement="top"
              title={COST_BREAKDOWN_FIELDS.freightInbound.tooltip}
            >
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormCurrencyTextField
              key={`tax-${toggleCount}`}
              fullWidth
              label={moneyLabel(COST_BREAKDOWN_FIELDS.tax.label)}
              name="tax"
              control={control}
              lockTo={costCurrencyCode}
              compact
            />
            <Tooltip
              arrow
              placement="top"
              title={COST_BREAKDOWN_FIELDS.tax.tooltip}
            >
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormCurrencyTextField
              key={`tariffs-${toggleCount}`}
              fullWidth
              label={moneyLabel(COST_BREAKDOWN_FIELDS.tariffs.label)}
              name="tariffs"
              control={control}
              lockTo={costCurrencyCode}
              compact
            />
            <Tooltip
              arrow
              placement="top"
              title={COST_BREAKDOWN_FIELDS.tariffs.tooltip}
            >
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormCurrencyTextField
              key={`interest-${toggleCount}`}
              fullWidth
              label={moneyLabel(COST_BREAKDOWN_FIELDS.interest.label)}
              name="interest"
              control={control}
              lockTo={costCurrencyCode}
              compact
            />
            <Tooltip
              arrow
              placement="top"
              title={COST_BREAKDOWN_FIELDS.interest.tooltip}
            >
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Stack>
        </Box>

        <Typography variant="subtitle1" paddingTop={4} paddingBottom={2}>
          Deployment costs
        </Typography>
        <Box display="grid" gridTemplateColumns="1fr" rowGap={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormCurrencyTextField
              key={`freightOutbound-${toggleCount}`}
              fullWidth
              label={moneyLabel(COST_BREAKDOWN_FIELDS.freightOutbound.label)}
              name="freightOutbound"
              control={control}
              lockTo={costCurrencyCode}
              compact
            />
            <Tooltip
              arrow
              placement="top"
              title={COST_BREAKDOWN_FIELDS.freightOutbound.tooltip}
            >
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormCurrencyTextField
              key={`installation-${toggleCount}`}
              fullWidth
              label={moneyLabel(COST_BREAKDOWN_FIELDS.installation.label)}
              name="installation"
              control={control}
              lockTo={costCurrencyCode}
              compact
            />
            <Tooltip
              arrow
              placement="top"
              title={COST_BREAKDOWN_FIELDS.installation.tooltip}
            >
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
